import { Injectable } from '@angular/core';
import { RouteReuseStrategy, ActivatedRouteSnapshot, DetachedRouteHandle } from '@angular/router';
import { isEmpty } from 'lodash/index';

// RouteReuseStrategy documentation: https://angular.dev/guide/routing/customizing-route-behavior
// BaseRouteReuseStrategy source code: https://github.com/angular/angular/blob/main/packages/router/src/route_reuse_strategy.ts
@Injectable()
export class CommonRouteReuseStrategy implements RouteReuseStrategy {
  private readonly _store = new Map<string | null, DetachedRouteHandle>();
  private _nextRoute: ActivatedRouteSnapshot | null = null;
  private _currRoute: ActivatedRouteSnapshot | null = null;

  public get nextRouteKey(): string | null {
    return this._getRouteKey(this._nextRoute);
  }

  public get currRouteKey(): string | null {
    return this._getRouteKey(this._currRoute);
  }

  // shouldDetach and shouldAttach methods are only executed when this returns false!
  public shouldReuseRoute(next: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    this._nextRoute = next;
    this._currRoute = curr;

    return next.routeConfig === curr.routeConfig;
  }

  public shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return this._isRouteReusable(route) && this._isInRouteReuseWhenFrom(route, this.nextRouteKey);
  }

  // store method is only executed when shouldDetach returns true
  public store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = this._getRouteKey(route);

    if (key && handle) {
      // only store one route at a time
      this._store.clear();
      this._store.set(key, handle);
    }
  }

  public shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return this._store.has(this._getRouteKey(route)) && this._isRouteReusable(route) && this._isInRouteReuseWhenFrom(route, this.currRouteKey);
  }

  // retrieve method is only executed when shouldAttach returns true
  public retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    return this._store.get(this._getRouteKey(route)) ?? null;
  }

  private _getRouteKey(route: ActivatedRouteSnapshot | null): string | null {
    return route?.data.routeKey ?? null;
  }

  private _isRouteReusable(route: ActivatedRouteSnapshot): boolean {
    return !isEmpty(route.data.reuseWhenFrom);
  }

  private _getRouteReuseWhenFrom(route: ActivatedRouteSnapshot): Array<string> {
    const reuseWhenFrom: Array<string> | string = route.data.reuseWhenFrom || [];

    return Array.isArray(reuseWhenFrom) ? reuseWhenFrom : [reuseWhenFrom];
  }

  private _isInRouteReuseWhenFrom(route: ActivatedRouteSnapshot, key: string | null): boolean {
    if (!key) {
      return false;
    }

    return this._getRouteReuseWhenFrom(route).includes(key);
  }
}
